import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@workspace/ui/components/dialog';
import { CheckCircle2, Terminal } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '~/auth/use-auth';
import { m } from '~/paraglide/messages.js';
import { CLI_AUTH_SUCCESS_KEY } from '~/routes/_authenticated/cli/-config/permissions';
import { CliCommandWidget } from './cli-command-widget';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ConnectAgentDialog({ open, onOpenChange }: Props) {
  const { updateMetadata } = useAuth();
  const [connected, setConnected] = useState(false);

  const handleConnected = useCallback(() => {
    setConnected(true);
    updateMetadata({ agent_connected: true });
    setTimeout(() => {
      onOpenChange(false);
      setConnected(false);
    }, 2000);
  }, [updateMetadata, onOpenChange]);

  useEffect(() => {
    if (!open) return;

    const handler = (e: StorageEvent) => {
      if (e.key === CLI_AUTH_SUCCESS_KEY) {
        localStorage.removeItem(CLI_AUTH_SUCCESS_KEY);
        handleConnected();
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [open, handleConnected]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {connected ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-green-500/10">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </div>
            <div className="space-y-1 text-center">
              <DialogTitle className="text-xl font-semibold">
                {m['connect_agent.connected']()}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                {m['cli_auth.success_description']()}
              </DialogDescription>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <Terminal className="h-6 w-6 text-primary" />
              </div>
              <div className="space-y-1 text-center">
                <DialogTitle className="text-xl font-semibold">
                  {m['connect_agent.dialog_title']()}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  {m['connect_agent.dialog_description']()}
                </DialogDescription>
              </div>
            </div>

            <CliCommandWidget />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
